import {
	Box,
	Typography,
	useTheme,
	Button,
	Stack,
	Chip,
	Alpha,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import { alpha } from "@mui/material/styles";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";

const PermissionPage = () => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const navigate = useNavigate();

	const handleBack = () => {
		navigate("/");
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
				px: { xs: 2, sm: 3, md: 4 },
				py: { xs: 4, md: 6 },
				background:
					theme.palette.mode === "dark"
						? `
							radial-gradient(circle at top left, ${alpha(colors.blueAccent[500], 0.22)} 0%, transparent 32%),
							radial-gradient(circle at bottom right, ${alpha(colors.pink[500], 0.18)} 0%, transparent 34%),
							linear-gradient(135deg, #0b1020 0%, #111827 45%, #0f172a 100%)
						  `
						: `
							radial-gradient(circle at top left, ${alpha(colors.blueAccent[400], 0.16)} 0%, transparent 30%),
							radial-gradient(circle at bottom right, ${alpha(colors.pink[400], 0.14)} 0%, transparent 32%),
							linear-gradient(135deg, #f8fbff 0%, #f4f7fb 50%, #eef3f8 100%)
						  `,
			}}
		>
			<Box
				sx={{
					position: "absolute",
					top: -120,
					left: -120,
					width: 260,
					height: 260,
					borderRadius: "50%",
					background: alpha(colors.blueAccent[500], 0.16),
					filter: "blur(30px)",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					bottom: -120,
					right: -100,
					width: 260,
					height: 260,
					borderRadius: "50%",
					background: alpha(colors.pink[500], 0.14),
					filter: "blur(34px)",
				}}
			/>

			<Box
				sx={{
					width: "100%",
					maxWidth: 760,
					position: "relative",
					borderRadius: "28px",
					overflow: "hidden",
					border: `1px solid ${alpha(
						theme.palette.mode === "dark" ? "#ffffff" : colors.grey[900],
						0.08
					)}`,
					background:
						theme.palette.mode === "dark"
							? alpha("#111827", 0.78)
							: alpha("#ffffff", 0.82),
					backdropFilter: "blur(18px)",
					boxShadow:
						theme.palette.mode === "dark"
							? "0 24px 80px rgba(0,0,0,0.38)"
							: "0 24px 80px rgba(15, 23, 42, 0.12)",
				}}
			>
				<Box
					sx={{
						height: 8,
						background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.pink[500]} 100%)`,
					}}
				/>

				<Box
					sx={{
						p: { xs: 3, sm: 4, md: 5 },
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
					}}
				>
					<Chip
						icon={<SecurityRoundedIcon />}
						label="Acesso restrito"
						sx={{
							mb: 3,
							px: 1,
							height: 36,
							fontWeight: 700,
							borderRadius: "999px",
							color: theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
							backgroundColor: alpha(colors.blueAccent[500], 0.12),
							border: `1px solid ${alpha(colors.blueAccent[500], 0.25)}`,
							"& .MuiChip-icon": {
								color: colors.blueAccent[400],
							},
						}}
					/>

					<Box
						sx={{
							width: { xs: 82, sm: 96 },
							height: { xs: 82, sm: 96 },
							borderRadius: "24px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mb: 3,
							background: `linear-gradient(135deg, ${alpha(
								colors.pink[500],
								0.18
							)} 0%, ${alpha(colors.blueAccent[500], 0.2)} 100%)`,
							border: `1px solid ${alpha(colors.blueAccent[400], 0.18)}`,
							boxShadow: `0 18px 40px ${alpha(colors.blueAccent[500], 0.18)}`,
						}}
					>
						<LockOutlinedIcon
							sx={{
								fontSize: { xs: 40, sm: 46 },
								color: theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
							}}
						/>
					</Box>

					<Typography
						sx={{
							fontSize: { xs: "1.8rem", sm: "2.3rem", md: "2.8rem" },
							fontWeight: 900,
							lineHeight: 1.1,
							letterSpacing: "-0.03em",
							color: theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
							maxWidth: 560,
						}}
					>
						Você não tem permissão para acessar esta página
					</Typography>

					<Typography
						sx={{
							mt: 2,
							maxWidth: 560,
							fontSize: { xs: "0.98rem", sm: "1.05rem" },
							lineHeight: 1.7,
							color:
								theme.palette.mode === "dark"
									? alpha("#ffffff", 0.72)
									: alpha(colors.grey[900], 0.7),
						}}
					>
						Seu perfil atual não possui acesso a este módulo. Caso isso esteja
						incorreto, verifique suas permissões com o administrador do sistema.
					</Typography>

					<Box
						sx={{
							mt: 4,
							width: "100%",
							maxWidth: 560,
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							gap: 2,
						}}
					>
						<Box
							sx={{
								p: 2.2,
								borderRadius: "18px",
								textAlign: "left",
								backgroundColor: alpha(
									theme.palette.mode === "dark" ? "#ffffff" : colors.grey[900],
									0.04
								),
								border: `1px solid ${alpha(
									theme.palette.mode === "dark" ? "#ffffff" : colors.grey[900],
									0.08
								)}`,
							}}
						>
							<Typography
								sx={{
									fontSize: "0.95rem",
									fontWeight: 800,
									mb: 0.8,
									color:
										theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
								}}
							>
								Por que estou vendo isso?
							</Typography>
							<Typography
								sx={{
									fontSize: "0.92rem",
									lineHeight: 1.6,
									color:
										theme.palette.mode === "dark"
											? alpha("#ffffff", 0.68)
											: alpha(colors.grey[900], 0.68),
								}}
							>
								Essa rota exige permissões específicas ou um tipo de usuário
								diferente do atual.
							</Typography>
						</Box>

						<Box
							sx={{
								p: 2.2,
								borderRadius: "18px",
								textAlign: "left",
								backgroundColor: alpha(
									theme.palette.mode === "dark" ? "#ffffff" : colors.grey[900],
									0.04
								),
								border: `1px solid ${alpha(
									theme.palette.mode === "dark" ? "#ffffff" : colors.grey[900],
									0.08
								)}`,
							}}
						>
							<Typography
								sx={{
									fontSize: "0.95rem",
									fontWeight: 800,
									mb: 0.8,
									color:
										theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
								}}
							>
								Como resolver?
							</Typography>
							<Typography
								sx={{
									fontSize: "0.92rem",
									lineHeight: 1.6,
									color:
										theme.palette.mode === "dark"
											? alpha("#ffffff", 0.68)
											: alpha(colors.grey[900], 0.68),
								}}
							>
								Volte para a página inicial ou solicite liberação de acesso para
								continuar.
							</Typography>
						</Box>
					</Box>

					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						sx={{
							mt: 4.5,
							width: "100%",
							maxWidth: 460,
							justifyContent: "center",
						}}
					>
						<Button
							onClick={handleBack}
							variant="contained"
							startIcon={<HomeRoundedIcon />}
							sx={{
								flex: 1,
								height: 52,
								borderRadius: "16px",
								fontWeight: 800,
								textTransform: "none",
								fontSize: "0.98rem",
								background: `linear-gradient(90deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
								boxShadow: `0 14px 30px ${alpha(colors.blueAccent[600], 0.32)}`,
								"&:hover": {
									background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
								},
							}}
						>
							Voltar para Home
						</Button>

						<Button
							onClick={() => navigate(-1)}
							variant="outlined"
							startIcon={<ArrowBackRoundedIcon />}
							sx={{
								flex: 1,
								height: 52,
								borderRadius: "16px",
								fontWeight: 800,
								textTransform: "none",
								fontSize: "0.98rem",
								borderColor: alpha(colors.grey[500], 0.3),
								color:
									theme.palette.mode === "dark" ? "#fff" : colors.grey[900],
								"&:hover": {
									borderColor: colors.blueAccent[500],
									backgroundColor: alpha(colors.blueAccent[500], 0.06),
								},
							}}
						>
							Voltar
						</Button>
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default PermissionPage;